import { COLORS, FONTS } from '../styles/tokens';
import type { ColorTheme } from '../styles/tokens';
import { inputStyle as sharedInputStyle, labelStyle as sharedLabelStyle } from '../styles/admin-styles';
import type { OrgTreeNode } from '../api/keycloak-admin/org-config';

/** Plugin entry from API */
export interface PluginEntry {
  id: string;
  name: string;
  description: string;
  sectors: string[];
}

interface UnitConfigPanelProps {
  node: OrgTreeNode | null;
  plugins: PluginEntry[];
  orgPlugins: string[];
  isDark: boolean;
  colors: ColorTheme;
  onUpdate: (updates: Partial<OrgTreeNode>) => void;
}

/** Right column: unit configuration panel or placeholder */
export function UnitConfigPanel({
  node, plugins, orgPlugins, isDark, colors, onUpdate,
}: UnitConfigPanelProps): React.JSX.Element {
  const iStyle = sharedInputStyle(isDark, colors);
  const lStyle = sharedLabelStyle(colors);

  return (
    <div className="vx-fill-col" style={{
      flex: '1 1 320px', minWidth: '280px',
      border: `1px solid ${colors.border}`, borderRadius: '10px',
      padding: '16px', overflow: 'auto', maxHeight: '600px',
      backgroundColor: isDark ? COLORS.dark.card : COLORS.light.card,
    }}>
      {!node ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <span style={{ fontFamily: FONTS.body, fontSize: '13px', color: colors.textSecondary }}>
            Select a unit to configure
          </span>
        </div>
      ) : (
        <UnitConfigContent node={node} plugins={plugins} orgPlugins={orgPlugins}
          isDark={isDark} colors={colors} inputStyle={iStyle} labelStyle={lStyle}
          onUpdate={onUpdate} />
      )}
    </div>
  );
}

/** Unit configuration form content */
function UnitConfigContent({ node, plugins, orgPlugins, isDark, colors, inputStyle, labelStyle, onUpdate }: {
  node: OrgTreeNode; plugins: PluginEntry[]; orgPlugins: string[];
  isDark: boolean; colors: ColorTheme;
  inputStyle: React.CSSProperties; labelStyle: React.CSSProperties;
  onUpdate: (updates: Partial<OrgTreeNode>) => void;
}): React.JSX.Element {
  const orgSet = new Set(orgPlugins);
  const unitSet = new Set(node.plugins);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      <div style={{ fontFamily: FONTS.heading, fontSize: '14px', color: colors.text, letterSpacing: '0.05em' }}>
        Unit Configuration
      </div>

      <FormField label="Name" value={node.name} onChange={(v) => onUpdate({ name: v })} inputStyle={inputStyle} labelStyle={labelStyle} />
      <FormField label="Short Name" value={node.shortName} onChange={(v) => onUpdate({ shortName: v })} inputStyle={inputStyle} labelStyle={labelStyle} />
      <FormField label="Type" value={node.type} onChange={(v) => onUpdate({ type: v })} inputStyle={inputStyle} labelStyle={labelStyle} />

      <div>
        <div style={labelStyle}>Echelon</div>
        <select value={node.echelon} onChange={(e) => onUpdate({ echelon: e.target.value as OrgTreeNode['echelon'] })}
          style={{ ...inputStyle, cursor: 'pointer' }}>
          <option value="strategic">Strategic</option>
          <option value="operational">Operational</option>
          <option value="tactical">Tactical</option>
        </select>
      </div>

      <div>
        <div style={labelStyle}>Plugins</div>
        {plugins.map((plugin) => {
          const inherited = orgSet.has(plugin.id);
          const enabled = unitSet.has(plugin.id);
          const label = inherited && enabled ? '(inherited)' : enabled ? '(added)' : '';
          const labelColor = inherited ? '#4ADE80' : '#01A9ED';

          return (
            <label key={plugin.id} style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              padding: '4px 8px', borderRadius: '4px', cursor: 'pointer',
              backgroundColor: (enabled || inherited) ? (isDark ? 'rgba(1,169,237,0.06)' : 'rgba(1,101,198,0.04)') : 'transparent',
            }}>
              <input type="checkbox" checked={enabled || inherited}
                onChange={() => {
                  const next = unitSet.has(plugin.id) ? node.plugins.filter(id => id !== plugin.id) : [...node.plugins, plugin.id];
                  onUpdate({ plugins: next });
                }}
                style={{ accentColor: '#01A9ED' }} />
              <span style={{ fontFamily: FONTS.body, fontSize: '12px', color: colors.text }}>{plugin.name}</span>
              {label && <span style={{ fontFamily: FONTS.body, fontSize: '10px', color: labelColor }}>{label}</span>}
            </label>
          );
        })}
      </div>

      <div>
        <div style={labelStyle}>Map Defaults</div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <NumField label="Lat" value={node.mapView.lat} onChange={(v) => onUpdate({ mapView: { ...node.mapView, lat: v } })} inputStyle={inputStyle} labelStyle={labelStyle} />
          <NumField label="Lng" value={node.mapView.lng} onChange={(v) => onUpdate({ mapView: { ...node.mapView, lng: v } })} inputStyle={inputStyle} labelStyle={labelStyle} />
          <NumField label="Zoom" value={node.mapView.zoom} onChange={(v) => onUpdate({ mapView: { ...node.mapView, zoom: v } })} inputStyle={inputStyle} labelStyle={labelStyle} />
        </div>
      </div>
    </div>
  );
}

/** Text form field */
function FormField({ label, value, onChange, inputStyle, labelStyle }: {
  label: string; value: string; onChange: (v: string) => void;
  inputStyle: React.CSSProperties; labelStyle: React.CSSProperties;
}): React.JSX.Element {
  return (
    <div>
      <div style={labelStyle}>{label}</div>
      <input type="text" value={value} onChange={(e) => onChange(e.target.value)} style={inputStyle} />
    </div>
  );
}

/** Number form field */
function NumField({ label, value, onChange, inputStyle, labelStyle }: {
  label: string; value: number; onChange: (v: number) => void;
  inputStyle: React.CSSProperties; labelStyle: React.CSSProperties;
}): React.JSX.Element {
  return (
    <div style={{ flex: 1 }}>
      <div style={{ ...labelStyle, fontSize: '8px' }}>{label}</div>
      <input type="number" value={value} step="0.1"
        onChange={(e) => onChange(parseFloat(e.target.value) || 0)} style={inputStyle} />
    </div>
  );
}
